/**
 * Selector de clientes hijo (GET /teams-child paginado) para contexto 3PL:
 * listado con scroll infinito y búsqueda por nombre (igual patrón que facturación).
 */

import { createHttpClient } from '@adapters/HttpClient';
import { ClientsApiAdapter } from '@modules/Clients/List/adapters/ClientsApiAdapter';
import type { DynamicComboboxOption } from '@shared/components/ui/DynamicCombobox';
import { apiClientWH } from '@shared/services/ApiClient';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

export interface UseTenantChildDynamicComboboxOptions {
  parentTeamId: number | undefined;
  enabled: boolean;
}

export const useTenantChildDynamicCombobox = ({ parentTeamId, enabled }: UseTenantChildDynamicComboboxOptions) => {
  const httpClient = useMemo(() => createHttpClient(apiClientWH), []);
  const adapter = useMemo(() => new ClientsApiAdapter(httpClient), [httpClient]);

  const scopedEnabled = Boolean(enabled && parentTeamId != null && parentTeamId > 0);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isFetched, error } = useInfiniteQuery({
    queryKey: ['tenant-child-dynamic-combobox', parentTeamId],
    queryFn: ({ pageParam = 1 }) =>
      adapter.getClientsPaginated({
        page: pageParam as number,
        limit: 40,
        sortBy: 'name',
        sortDirection: 'asc',
        parent_team_id: parentTeamId as number,
      }),
    getNextPageParam: (lastPage) => lastPage.nextPage,
    initialPageParam: 1,
    enabled: scopedEnabled,
    staleTime: 2 * 60 * 1000,
  });

  const allClients = useMemo(() => data?.pages.flatMap((p) => p.clients) ?? [], [data]);

  const browseTotal = data?.pages[0]?.total;

  const searchFn = useCallback(
    async (query: string): Promise<DynamicComboboxOption[]> => {
      if (!scopedEnabled || parentTeamId == null) return [];
      const q = query.trim();
      if (q.length >= 2) {
        const page = await adapter.getClientsPaginated({
          page: 1,
          limit: 100,
          sortBy: 'name',
          sortDirection: 'asc',
          name: q,
          parent_team_id: parentTeamId,
        });
        return page.clients.map((c) => ({
          label: c.name,
          value: String(c.id),
        }));
      }
      const source = !q ? allClients : allClients.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()));
      return source.map((c) => ({
        label: c.name,
        value: String(c.id),
      }));
    },
    [adapter, allClients, parentTeamId, scopedEnabled],
  );

  const onLoadMore = useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const resolveSelectedOption = useCallback(
    async (value: string): Promise<DynamicComboboxOption | null> => {
      const id = Number(value);
      if (!value || Number.isNaN(id)) return null;
      try {
        const c = await adapter.getClientById(id);
        return { label: c.name, value: String(c.id) };
      } catch {
        return null;
      }
    },
    [adapter],
  );

  return {
    searchFn,
    onLoadMore,
    hasMore: hasNextPage ?? false,
    isLoadingBrowse: isLoading,
    isFetchedBrowse: isFetched,
    browseTotal,
    resolveSelectedOption,
    fetchError: scopedEnabled && error instanceof Error ? error.message : error ? String(error) : null,
  };
};
